import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  RowNode,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  RootState,
  selectActiveSessionIdentifier,
  selectIsInternalUser,
} from '@helios/state-management'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter, parseISO } from 'date-fns'
import { take } from 'rxjs/operators'
import { InventoryType } from '../../../../model/inventory-type.model'
import { PricingResponse } from '../../../../model/pricing.model'
import { Security } from '../../../../model/security.model'
import { AccountDetails } from '../../../../model/single-transaction/account-details.model'
import { ExternalUnmatchedTransaction } from '../../../../model/single-transaction/external-unmatched-transaction'
import {
  lotLinkTypesVal,
  tranLinkToLotLinkMap,
} from '../../../../model/single-transaction/lotLinks.model'
import { TransactionModalInput } from '../../../../model/single-transaction/popup-input.model'
import {
  GenericGridTransaction,
  SecurityGrid,
  SecurityMappingModel,
  SecurityTransfer,
  TransferPositionData,
  securityField,
} from '../../../../model/single-transaction/security-transfer/security-transfer.model'
import { TransferOptions } from '../../../../model/single-transaction/security-transfer/transfer-options.model'
import { SleeveSimpleAccounts } from '../../../../model/single-transaction/sleeve-simple-accounts.model'
import {
  ENTRIES,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../../model/transaction-type.model'
import { CopyService } from '../../../../services/copy.service'
import { CreateEditTransactionService } from '../../../../services/create-edit-transaction.service'
import { PasteService } from '../../../../services/single-transaction/paste.service'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  addWarningDetails,
  clearAllInvalidations,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  getStringISOForDate,
  handleValidation,
  internalAssetIdFilterGridOptions,
} from '../../../../util/common-utils'
import { TransactionConstants } from '../../../../util/constants'
import { isEditModal } from '../../../../util/popup-input-util'
import {
  getCollateralBroker,
  getGenericData,
  getInitialLoadExtraData,
} from '../../../../util/security-transfer.initial-data'
@Component({
  selector: 'hs-security-generic-transfer',
  templateUrl: './security-transfer-generic-grid.component.html',
  styleUrls: ['./security-transfer-generic-grid.component.scss'],
  providers: [PasteService, CopyService],
})
export class SecurityTransferGenericGridComponent implements OnDestroy {
  private _aggregateAccountUnresolved = false
  @Input() transInput: TransactionModalInput | undefined
  @Input() set genericData(
    genericData: GenericGridTransaction | SecurityGrid | undefined,
  ) {
    if (!genericData) return

    this.accountId = genericData.accountDetails?.accountId
    this.rowData
      .filter(row => row.field.includes('Date') && row.value != '')
      .forEach(rowFiltered => {
        rowFiltered.value = genericData.reconDate.toString()
      })
    if ('transaction' in genericData) {
      if ('externalTransactionId' in genericData.transaction) {
        this.updateGridExt(genericData)
      } else if ('sessionReconDate' in genericData.transaction) {
        this.updateGridForPositionData(genericData)
      } else {
        this.updateGrid(genericData)
      }
      this.allLoadedGeneric.emit()
    } else {
      this.setupInitialData()
      this.loadTable = true
      this.allLoadedGeneric.emit()
    }
    this.fillCommonData(genericData)
    if (this.accountId) this.determineIfAccountIsCollateral(this.accountId)
  }

  @Output()
  genericFieldChanged = new EventEmitter<void>()
  @Output()
  transferDateSync = new EventEmitter<string>()
  @Output()
  transferPriceSync = new EventEmitter<string>()
  @Output()
  validUnitChange = new EventEmitter<number>()
  @Output()
  transferTypeChanged = new EventEmitter<TransferOptions>()
  @Output()
  allLoadedGeneric = new EventEmitter<void>()
  @Output()
  securityDetailsUpdated = new EventEmitter<Security>()
  @Output()
  editingStarted = new EventEmitter<void>()
  @Output()
  editingStopped = new EventEmitter<void>()
  @Output()
  isDataInvalidated = new EventEmitter<void>()
  @Output()
  isCollateralAccount = new EventEmitter<boolean>()
  @Output()
  pricesChangeByUnit = new EventEmitter<void>()

  @Input() errorText?: string

  loadingText = TransactionConstants.LOAD_TRANSACTION

  @Output()
  errorDetails = new EventEmitter<unknown>()

  @Output()
  errorHeader = new EventEmitter<string>()

  private _sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  @Input() set sleeveSimpleAccount(sleeveAccounts: SleeveSimpleAccounts[]) {
    if (sleeveAccounts?.length > 0) {
      this._sleeveSimpleAccount = sleeveAccounts
      const accountRowField = this.rowData?.find(row => row.id == 'account')
      if (accountRowField) {
        accountRowField.isEditable = true
      }
      // Accounts just arrived; if we're waiting on a sub-account choice, open the
      // picker now that it has options to show.
      if (this._aggregateAccountUnresolved && this.gridApi?.api) {
        focusFieldRowEditor(this.gridApi.api, 'account')
      }
    }
  }

  get sleeveSimpleAccount() {
    return this._sleeveSimpleAccount
  }

  @Output()
  sleeveAccountDetails = new EventEmitter<AccountDetails>()

  @Output() gridFocused = new EventEmitter<string>()

  gridApi?: AgGridApi
  transactionLoaded = false
  rowNodeMap = new Map<string, RowNode>()
  loadTable = false
  windowDate?: string
  rowData: SecurityMappingModel[] = getGenericData(
    getStringISOForDate(new Date()),
  )
  settleDate?: string
  entryDate?: string
  inventoryType = Object.keys(InventoryType)
  selectedTypeofTransfer = TransactionConstants.TRANSFER_OPTIONS[0]
  sessionId?: string
  accountId?: number
  userId?: number
  reconDate?: Date
  note = new FormControl('')
  collateralBroker?: string
  private isInternalUser = false

  /**
   * PVAL-25738: external users must not see the internal Asset ID row when
   * editing/viewing an existing transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return !this.isInternalUser && isEditModal(this.transInput)
  }

  private readonly colDefs: ColDef[] = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    public createEditTransactionService: CreateEditTransactionService,
    readonly store: Store<RootState>,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
  ) {
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .subscribe(isInternalUser => {
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      })
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: securityField('isEditable'),
        hide: true,
      },
      {
        field: securityField('isVisible'),
        hide: true,
      },
      {
        field: securityField('errorMessage'),
        hide: true,
      },
      {
        field: securityField('validityStatus'),
        headerName: '',
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
      },
      {
        field: securityField('field'),
        headerName: 'Field',
        maxWidth: 155,
        minWidth: 155,
      },
      {
        field: securityField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  @HostListener('click') genericGridClicked() {
    this.gridFocused.emit('generic')
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => {
        this.gridApi = event
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.editingStarted.emit()
      },
      onCellEditingStopped: () => {
        this.editingStopped.emit()
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
          const categoryNode = this.rowNodeMap.get('tranType')
          if (categoryNode?.data.value == '') {
            handleValidation(categoryNode, 'INVALID', 'Valid Value Required')
            this.isDataInvalidated.emit()
          }
        }
      },
      getRowNodeId: ({ id }: SecurityMappingModel) => id,
      columnDefs: this.colDefs,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFirstDataRendered: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandlerSingleDialog(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: event.rowIndex ?? 0,
            colKey: event.column.getColId(),
          })
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  // When the parent has an unresolved aggregate account, defer validation and flag the
  // account picker red until a concrete sub-account is chosen.
  @Input() set aggregateAccountUnresolved(value: boolean) {
    this._aggregateAccountUnresolved = value
    if (value) {
      // Mark the account row red so the user knows it must be picked before validation.
      // Mutate rowData (not a grid node) so it renders even if the grid isn't ready yet.
      const accountRowField = this.rowData?.find(row => row.id == 'account')
      if (accountRowField) {
        accountRowField.validityStatus = 'invalid'
        accountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
    }
    this.gridApi?.api.refreshCells({ force: true })
    // Only open the picker once its options have loaded; otherwise the select
    // opens stuck on its (static) loading state.
    if (value && this.sleeveSimpleAccount.length > 0 && this.gridApi?.api) {
      focusFieldRowEditor(this.gridApi.api, 'account')
    }
  }
  get aggregateAccountUnresolved(): boolean {
    return this._aggregateAccountUnresolved
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  /* Define dataType for all fields */
  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'entryDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'units':
      case 'transferPrice':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'tranType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.getTransferOptions(),
            bindValue: 'name',
            bindLabel: 'name',
          },
        }
      case 'inventoryType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(this.inventoryType),
          },
        }
      case 'account':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'displayLabel',
            bindLabel: 'displayLabel',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  async onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }
    const securityData: SecurityMappingModel = event.data
    switch (securityData.id) {
      case 'tranType':
        this.typeOfTransferOnChange(event.data['value'])
        break
      case 'entryDate':
        this.transferDatePropertyChange()
        this.fetchSecurityId(event)
        break
      case 'transferPrice':
        this.transferPriceChanged()
        break
      case 'units':
        this.onUnitsChange()
        break
      case 'cusip':
        this.fetchSecurityId(event)
        break
      case 'account':
        await this.loadAggAccountDetails(event)
        break
      case 'inventoryType':
        this.adjustUnitsOnInventoryChange(event.data['value'])
        break
    }
    if (securityData.id != 'account') {
      if (this.aggregateAccountUnresolved) {
        // No account yet: flag the edited field instead of triggering validation.
        addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
        event.api.refreshCells({ force: true, rowNodes: [event.node] })
      } else {
        this.genericFieldChanged.emit()
      }
    }
  }

  async loadAggAccountDetails(event: CellValueChangedEvent) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == event.data['value'],
    )
    if (account) {
      const accountDetails = await this.createEditTransactionService
        .loadAccountDetails(account.id)
        .toPromise()

      this.accountId = accountDetails.accountId
      // A concrete account is now resolved; clear the account-unresolved marks before the
      // parent re-validates. The parent also drives the @Input, this keeps the grid in sync.
      this._aggregateAccountUnresolved = false
      clearAllInvalidations(this.gridApi?.api)
      this.sleeveAccountDetails.emit(accountDetails)
      this.fetchSecurityId(event)
    }
  }

  fetchSecurityId(event: CellValueChangedEvent) {
    const entryDate =
      event.data.id == 'entryDate'
        ? event.data['value']
        : this.getRowNode('entryDate')?.data.value
    const cusip =
      event.data.id == 'cusip'
        ? event.data['value']
        : this.getRowNode('cusip')?.data.value

    if (!cusip || !this.accountId || isEditModal(this.transInput)) return

    this.createEditTransactionService
      .loadSecurityPrice(this.accountId, cusip, entryDate, this.sessionId ?? '')
      .subscribe((pricingResponse: PricingResponse) => {
        if (pricingResponse?.price) {
          this.getRowNode('transferPrice')?.setDataValue(
            'value',
            pricingResponse.price,
          )
        }
      })
    this.setSecurityDetailsFromCusip(cusip, entryDate)
  }

  /**
   * Set the security detail for the cusip.
   *
   * Get the originalCommission from the table and set the value
   */
  setSecurityDetailsFromCusip(cusip: string, entryDate: string) {
    // The cusip lookup is account-scoped; defer it until an account is picked.
    if (this.aggregateAccountUnresolved) return
    this.errorDetails.emit('')
    // accountId should be set here; if it isn't, the backend treats the 0 as
    // "no account" and uses the global (clientId=1) security scope, not account 0.
    this.createEditTransactionService
      .fetchSecurityDetailsFromCusip(cusip, entryDate, this.accountId ?? 0)
      .subscribe(
        securityDetails => {
          this.securityDetailsUpdated.emit(securityDetails)
        },
        err => {
          this.getRowNode('cusip')?.setDataValue('value', '')
          this.errorHeader.emit(TransactionConstants.ERROR_LOADING_SECURITY)
          this.errorDetails.emit(err.error)
        },
      )
  }

  setupInitialData() {
    this.rowData.forEach(element => {
      const tableNode = this.rowNodeMap.get(element.id)

      if (tableNode) {
        tableNode.data = element
        this.gridApi?.api.redrawRows({ rowNodes: [tableNode] })
      }
    })
  }

  updateGrid(genericData: GenericGridTransaction) {
    const transaction = genericData.transaction as SecurityTransfer
    this.collateralBroker = transaction.dataMap?.collateralBroker
    this.getRowNode('collateralBroker')?.setDataValue(
      'value',
      transaction.dataMap?.collateralBroker,
    )
    const security = genericData.security

    this.getRowNode('inventoryType')?.setDataValue(
      'value',
      transaction.inventoryType,
    )
    this.getRowNode('transferPrice')?.setDataValue(
      'value',
      transaction.transferPrice,
    )

    this.selectedTypeofTransfer = this.getTransferOption(transaction.tranType)
    this.transferTypeChanged.emit(this.selectedTypeofTransfer)
    this.adjustClientLotIdNode()
    this.getRowNode('tranType')?.setDataValue(
      'value',
      this.selectedTypeofTransfer.name,
    )
    this.getRowNode('tranType')?.setDataValue('isEditable', false)
    this.getRowNode('units')?.setDataValue('value', transaction.units)

    this.settleDate = transaction.settleDate
    this.entryDate = transaction.entryDate

    this.getRowNode('entryDate')?.setDataValue('value', transaction.entryDate)
    if (transaction.transactionId) {
      this.createEditTransactionService
        .fetchTranLinkInfo(transaction.transactionId)
        .subscribe(tranLink => {
          if (tranLink) {
            this.getRowNode('sourceAccountId')?.setDataValue(
              'value',
              tranLink.sourceAccountId,
            )
            this.getRowNode('exchangeType')?.setDataValue(
              'value',
              this.getExchangeTypeFromTranType(tranLink.type),
            )
          }
        })
    }
    this.getRowNode('sourceAccountId')?.setDataValue('isEditable', false)
    this.getRowNode('exchangeType')?.setDataValue('isEditable', false)
    if (transaction.clientLotId && transaction.clientLotId != null)
      this.getRowNode('clientLotId')?.setDataValue(
        'value',
        transaction.clientLotId,
      )
    if (security.securityMasterData) {
      this.getRowNode('cusip')?.setDataValue(
        'value',
        security.securityMasterData.cusip,
      )
      this.getRowNode('cusip')?.setDataValue('isEditable', false)
    }
    this.getRowNode('cwanIdentifier')?.setDataValue(
      'value',
      transaction.cwanIdentifier ?? '',
    )
    this.loadTable = true
  }

  private fillCommonData(genericGrid: SecurityGrid) {
    if (genericGrid.accountDetails && !genericGrid.accountDetails.isAggregate) {
      this.getRowNode('account')?.setDataValue(
        'value',
        this.getAccountDisplayName(genericGrid.accountDetails),
      )
    } else if (genericGrid.accountDetails?.isAggregate) {
      const accountRowField = this.rowData?.find(row => row.id == 'account')
      if (accountRowField) {
        accountRowField.isEditable = true
        accountRowField.value = ''
      }
    }
  }

  updateGridForPositionData(genericData: GenericGridTransaction) {
    const transaction = genericData.transaction as TransferPositionData
    this.collateralBroker = transaction.collateralBroker
    this.getRowNode('tranType')?.setDataValue('value', 'Transfer Out')
    this.getRowNode('units')?.setDataValue('value', transaction.units)
    this.getRowNode('cusip')?.setDataValue('value', transaction.assetId)
    this.getRowNode('cwanIdentifier')?.setDataValue(
      'value',
      transaction.cwanIdentifier ?? '',
    )
    this.getRowNode('entryDate')?.setDataValue(
      'value',
      transaction.sessionReconDate,
    )
    this.getRowNode('inventoryType')?.setDataValue(
      'value',
      transaction.inventoryType,
    )
    this.getRowNode('collateralBroker')?.setDataValue(
      'value',
      transaction.collateralBroker,
    )
    this.loadTable = true
  }

  updateGridExt(genericGrid: GenericGridTransaction) {
    const transaction = genericGrid.transaction as ExternalUnmatchedTransaction
    if (transaction.tranType) {
      this.selectedTypeofTransfer = this.getTransferOption(transaction.tranType)
    }
    this.transferTypeChanged.emit(this.selectedTypeofTransfer)
    this.adjustClientLotIdNode()
    this.getRowNode('tranType')?.setDataValue(
      'value',
      this.selectedTypeofTransfer.name,
    )
    this.getRowNode('inventoryType')?.setDataValue(
      'value',
      this.inventoryType[0],
    )
    if (transaction.transferPrice) {
      this.getRowNode('transferPrice')?.setDataValue(
        'value',
        transaction.transferPrice,
      )
    }
    if (transaction.calcUnits) {
      this.getRowNode('units')?.setDataValue('value', transaction.calcUnits)
    }

    this.settleDate = transaction.settleDate
    this.entryDate = transaction.entryDate
    this.getRowNode('entryDate')?.setDataValue('value', transaction.entryDate)
    this.getRowNode('cusip')?.setDataValue(
      'value',
      genericGrid.security.securityMasterData.cusip,
    )
    this.getRowNode('cwanIdentifier')?.setDataValue(
      'value',
      transaction.cwanIdentifier ?? '',
    )
    this.collateralBroker = transaction.aggInfoJson?.collateralBroker
    if (
      transaction.custodyLotIdentifier &&
      transaction.custodyLotIdentifier != null
    ) {
      this.getRowNode('clientLotId')?.setDataValue(
        'value',
        transaction.custodyLotIdentifier,
      )
    }
    this.loadTable = true
  }

  typeOfTransferOnChange(transferType: TransactionTypeEnum) {
    if (this.selectedTypeofTransfer.name == transferType) return
    const transferOption = this.getTransferOptions().find(
      option => option.name == transferType,
    )
    this.selectedTypeofTransfer = transferOption ?? this.getTransferOptions()[0]
    this.transferTypeChanged.emit(this.selectedTypeofTransfer)
    if (
      this.selectedTypeofTransfer.name == TransactionTypeEnum.TRANSFER_OUT ||
      this.selectedTypeofTransfer.name == TransactionTypeEnum.EXCHANGE_OUT
    ) {
      this.syncDatesIfTransferOut(this.selectedTypeofTransfer.name)
      this.syncPrices(this.selectedTypeofTransfer.name)
    }
    const inventoryType = this.rowNodeMap.get('inventoryType')?.data.value
    this.syncUnits(this.selectedTypeofTransfer.name, inventoryType)
    this.adjustClientLotIdNode()
  }
  getAccountDisplayName(accountDetails: AccountDetails) {
    return accountDetails.accountId + ' - ' + accountDetails.accountName
  }

  transferPriceChanged() {
    this.syncPrices(this.selectedTypeofTransfer.name)
    //TASK 2. syncPrices
    //if tranType is TRANSFER_OUT || EXCHANGE_OUT { goto DefaultColumn and set ORIG_TRADE_PRICE }
  }

  transferDatePropertyChange() {
    this.syncDatesIfTransferOut(this.selectedTypeofTransfer.name)
  }

  syncDatesIfTransferOut(tranType: string) {
    if (
      tranType == TransactionTypeEnum.TRANSFER_OUT ||
      tranType == TransactionTypeEnum.EXCHANGE_OUT
    ) {
      this.transferDateSync.emit(this.getRowNode('entryDate')?.data.value)
    }
  }

  syncPrices(tranType: string) {
    if (
      tranType == TransactionTypeEnum.TRANSFER_OUT ||
      tranType == TransactionTypeEnum.EXCHANGE_OUT
    ) {
      this.transferPriceSync.emit(this.getRowNode('transferPrice')?.data.value)
    }
  }

  syncUnits(tranType: string, inventoryType: string) {
    if (!tranType && !inventoryType) return
    if (
      tranType == TransactionTypeEnum.TRANSFER_IN ||
      tranType == TransactionTypeEnum.EXCHANGE_IN ||
      tranType == TransactionTypeEnum.INITIAL_LOAD
    ) {
      if (inventoryType === 'LONG') {
        this.adjustPositiveUnits()
      } else if (inventoryType === 'SHORT') {
        this.adjustNegativeUnits()
      }
    } else if (
      tranType == TransactionTypeEnum.TRANSFER_OUT ||
      tranType == TransactionTypeEnum.EXCHANGE_OUT
    ) {
      if (inventoryType === 'LONG') {
        this.adjustNegativeUnits()
      } else if (inventoryType === 'SHORT') {
        this.adjustPositiveUnits()
      }
    }
  }

  adjustUnitsOnInventoryChange(inventoryType: string) {
    const transferType = this.rowNodeMap.get('tranType')?.data.value
    const transferOption = this.getTransferOptions().find(
      option => option.name == transferType || option.tranType == transferType,
    )
    this.syncUnits(transferOption?.name ?? '', inventoryType)
  }

  adjustPositiveUnits() {
    if (
      this.rowNodeMap.get('units')?.data.value !== '' &&
      Number(this.rowNodeMap.get('units')?.data.value) < 0
    ) {
      this.getRowNode('units')?.setDataValue(
        'value',
        Math.abs(this.rowNodeMap.get('units')?.data.value),
      )
    }
  }
  adjustNegativeUnits() {
    if (
      this.rowNodeMap.get('units')?.data.value !== '' &&
      Number(this.rowNodeMap.get('units')?.data.value) > 0
    ) {
      this.getRowNode('units')?.setDataValue(
        'value',
        -1 * Math.abs(this.rowNodeMap.get('units')?.data.value),
      )
    }
  }

  onUnitsChange() {
    //TASK 4. updateProceeds
    const transferType = this.rowNodeMap.get('tranType')?.data.value
    const transferOption = this.getTransferOptions().find(
      option => option.name == transferType || option.tranType == transferType,
    )
    this.syncUnits(
      transferOption?.name ?? '',
      this.rowNodeMap.get('inventoryType')?.data.value,
    )
    this.updateProceeds()
    this.pricesChangeByUnit.emit()
  }

  getRowNode(key: string): RowNode | undefined {
    return this.rowNodeMap.get(key)
  }

  updateProceeds() {
    const transactionType = this.selectedTypeofTransfer.name
    if (
      !isEditModal(this.transInput) ||
      !ENTRIES.includes(transactionType) ||
      !this.settleDate ||
      !this.entryDate ||
      !isAfter(parseISO(this.settleDate), parseISO(this.entryDate))
    ) {
      return
    }
    const units =
      this.getRowNode('units')?.data.value == ''
        ? 1.0
        : Number(this.getRowNode('units')?.data.value)

    this.validUnitChange.emit(units)
  }

  //convert functions
  getExchangeTypeFromTranType(tranLinkType: string) {
    const lotLinkId = tranLinkToLotLinkMap.get(tranLinkType)
    return lotLinkId ? lotLinkTypesVal[lotLinkId].exchangeType : 'NORMAL'
  }

  getTransferOption(transType: TransactionTypeKey): TransferOptions {
    const option = TransactionConstants.TRANSFER_OPTIONS.filter(
      data => data.tranType === transType,
    )
    return option.length > 0
      ? option[0]
      : { tranType: transType, name: TransactionTypeEnum[transType] }
  }

  getTransferOptions() {
    return TransactionConstants.TRANSFER_OPTIONS
  }

  getUnits(): number {
    const units = this.getRowNode('units')?.data.value
    return units !== '' ? Number(this.getRowNode('units')?.data.value) : 0
  }

  markAllNodeValid() {
    this.rowNodeMap.forEach((value: RowNode) => {
      if (this.gridApi) this.handleValidation(value, 'VALID', '')
    })
  }

  handleValidation(
    rowNode: RowNode,
    validationState: string,
    validationMessage: string,
  ) {
    if (this.gridApi)
      handleValidation(rowNode, validationState, validationMessage)
  }

  adjustClientLotIdNode() {
    if (this.selectedTypeofTransfer.name == TransactionTypeEnum.INITIAL_LOAD) {
      this.gridApi?.api.applyTransaction({ add: getInitialLoadExtraData() })
      this.getRowNode('clientLotId')?.setDataValue(
        'isEditable',
        !isEditModal(this.transInput),
      )
    } else {
      if (this.getRowNode('clientLotId')) {
        this.gridApi?.api.applyTransaction({
          remove: getInitialLoadExtraData(),
        })
      }
    }
  }

  determineIfAccountIsCollateral(accountId: number) {
    this.createEditTransactionService
      .loadInventoryConfig(accountId)
      .subscribe(config => {
        if (config.inventoryGroupMethod == 'COLLATERAL_BROKER') {
          this.isCollateralAccount.emit(true)
          this.gridApi?.api.applyTransaction({
            add: getCollateralBroker(this.collateralBroker),
          })
        } else {
          this.isCollateralAccount.emit(false)
        }
      })
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }
}
